/**
 * View Transitions: feature-detect, `types`, `transition.finished`, reduced motion.
 * @see web-animation-view-transitions skill
 */

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const SUPPORTS_VIEW_TRANSITIONS =
  typeof document !== "undefined" && "startViewTransition" in document;

/** Must match `scNavPane*` keyframe duration in sc-header-nav-drawer.css */
const SC_NAV_PANE_ANIM_MS = 280;

/** If `animationend` never fires (slow device / tab background), force cleanup */
const SC_NAV_PANE_SWAP_FALLBACK_MS = SC_NAV_PANE_ANIM_MS + 120;

const SC_NAV_BELOW_SHIFT_EASING = "ease";

/** Passed to `startViewTransition({ types })` for `html:active-view-transition-type(...)` */
const VIEW_TRANSITION_TYPE_SC_NAV_FORWARD = "sc-nav-forward";
const VIEW_TRANSITION_TYPE_SC_NAV_BACK = "sc-nav-back";

/** Outgoing pane during swap — see sc-header-nav-drawer.css */
const SC_NAV_PANE_OUTGOING_OVERLAY_CLASS = "sc-header-nav-drawer__pane--outgoing-overlay";

/** Promo block fade when switching metaobject slot (see #syncSwitcherAndMediaForPaneSwap) */
const SC_NAV_MEDIA_FADE_MS = 220;

/** Must match `--sc-nav-drawer-duration` / `.sc-header-nav-drawer__sheet` in sc-header-nav-drawer.css */
const SC_NAV_DRAWER_PANEL_TRANSITION_MS = 350;
const SC_NAV_DRAWER_CLOSE_FALLBACK_MS = SC_NAV_DRAWER_PANEL_TRANSITION_MS + 80;

/** Syncs header ::before scrim fade with sheet close — see base.css */
const SC_NAV_DRAWER_SUMMARY_CLOSING_CLASS = "sc-header-nav-drawer__summary--closing";

class ScHeaderNavDrawer extends HTMLElement {
  constructor() {
    super();

    this.#details = this.querySelector("details");
    this.#summary = this.querySelector("summary");
    this.#panel = this.querySelector(".sc-header-nav-drawer__panel");
    this.#sheet = this.querySelector(".sc-header-nav-drawer__sheet");
    this.#viewport = this.querySelector("[data-viewport]");
    this.#dynamicRegion = this.querySelector("[data-dynamic-region]");
    this.#switcherRow = this.querySelector("[data-switcher-row]");
    this.#jsonEl = this.querySelector('script[type="application/json"]');

    this.#paneEls = this.#viewport ? Array.from(this.#viewport.querySelectorAll("[data-pane-layer]")) : [];
    this.#activePaneIndex = 0;

    this.#data = null;
    this.#labels = { back: "Back" };

    this.#branchHandle = null;
    this.#stack = [];
    this.#currentItems = [];
    this.#pageTitle = "";

    this.#reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);

    this.#paneTransitionActive = false;
    this.#queuedPaneSwap = null;
  }

  connectedCallback() {
    if (!this.#details || !this.#summary || !this.#jsonEl) return;

    try {
      this.#data = JSON.parse(this.#jsonEl.textContent);
      if (this.#data.labels) this.#labels = { ...this.#labels, ...this.#data.labels };
    } catch {
      this.#data = { switcher: [], rootFlat: [] };
    }

    this.#renderInitialRoot();

    this.#summary.addEventListener("click", this.#onSummaryClick);
    this.#details.addEventListener("toggle", this.#onDetailsToggle);
    this.addEventListener("click", this.#onHostClick);
    this.addEventListener("keyup", this.#onHostKeyup);

    if (this.#switcherRow) {
      this.#switcherRow.addEventListener("click", this.#onSwitcherClick);
    }
  }

  disconnectedCallback() {
    this.#summary?.removeEventListener("click", this.#onSummaryClick);
    this.#details?.removeEventListener("toggle", this.#onDetailsToggle);
    this.removeEventListener("click", this.#onHostClick);
    this.removeEventListener("keyup", this.#onHostKeyup);
    this.#switcherRow?.removeEventListener("click", this.#onSwitcherClick);
  }

  #details;
  #summary;
  #panel;
  #sheet;
  #viewport;
  #dynamicRegion;
  #switcherRow;
  #jsonEl;
  #paneEls;
  #activePaneIndex;
  #data;
  #labels;
  #branchHandle;
  #stack;
  #currentItems;
  #pageTitle;
  #reducedMotion;
  #paneTransitionActive;
  #queuedPaneSwap;

  #shouldEnableViewTransitions() {
    return SUPPORTS_VIEW_TRANSITIONS && !this.#reducedMotion.matches;
  }

  #onSummaryClick = (event) => {
    if (this.#details.hasAttribute("open")) {
      event.preventDefault();
      this.#closeDrawer();
    }
  };

  #onDetailsToggle = () => {
    if (this.#details.hasAttribute("open")) {
      this.#openDrawer();
    } else {
      this.#afterClose();
    }
  };

  #onHostKeyup = (event) => {
    if (event.code.toUpperCase() !== "ESCAPE") return;
    if (!this.#details.hasAttribute("open")) return;
    this.#closeDrawer();
  };

  #onHostClick = (event) => {
    const back = event.target.closest('[data-action="back"]');
    if (back) {
      event.preventDefault();
      this.#navigateBack();
      return;
    }

    const drill = event.target.closest('[data-action="drill"]');
    if (drill && this.#viewport?.contains(drill)) {
      event.preventDefault();
      const handle = drill.getAttribute("data-handle");
      if (!handle) return;
      this.#navigateDrill(handle);
      return;
    }

    const closeBtn = event.target.closest('[data-action="close-drawer"]');
    if (closeBtn) {
      event.preventDefault();
      this.#closeDrawer();
    }
  };

  #onSwitcherClick = (event) => {
    const btn = event.target.closest("[data-switcher-handle]");
    if (!btn) return;
    event.preventDefault();
    const handle = btn.getAttribute("data-switcher-handle");
    if (!handle) return;

    if (handle === this.#branchHandle) {
      this.#navigateSwitcherToRoot();
      return;
    }

    this.#enterBranch(handle, "forward");
  };

  #openDrawer() {
    this.#details.classList.remove("sc-header-nav-drawer__details--closing");
    this.#summary.classList.remove(SC_NAV_DRAWER_SUMMARY_CLOSING_CLASS);

    this.#header = this.#header || document.querySelector(".shopify-section-header");
    this.#borderOffset = this.#borderOffset || this.closest(".header-wrapper")?.classList.contains("header-wrapper--border-bottom") ? 1 : 0;

    if (this.#header) {
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(this.#header.getBoundingClientRect().bottom - this.#borderOffset)}px`,
      );
    }

    this.#summary.setAttribute("aria-expanded", "true");
    document.documentElement.classList.add(`overflow-hidden-${this.dataset.breakpoint || "tablet"}`);

    this.#resetNavState();
    this.#swapPaneContent(() => this.#buildRootHtml(), "forward", true);

    setTimeout(() => {
      if (this.#panel) trapFocus(this.#panel, this.#focusTarget());
    });
  }

  #header;
  #borderOffset;

  #afterClose() {
    this.#summary.setAttribute("aria-expanded", "false");
    document.documentElement.classList.remove(`overflow-hidden-${this.dataset.breakpoint || "tablet"}`);
    removeTrapFocus(this.#summary);
    this.#resetNavState();
    this.#renderInitialRoot();
  }

  #closeDrawer() {
    if (!this.#details.hasAttribute("open")) return;

    if (this.#reducedMotion.matches) {
      this.#details.removeAttribute("open");
      return;
    }

    if (this.#details.classList.contains("sc-header-nav-drawer__details--closing")) return;

    const sheet = this.#sheet;
    if (!sheet) {
      this.#details.removeAttribute("open");
      return;
    }

    this.#summary.classList.add(SC_NAV_DRAWER_SUMMARY_CLOSING_CLASS);
    this.#details.classList.add("sc-header-nav-drawer__details--closing");

    let finished = false;
    const complete = () => {
      if (finished) return;
      finished = true;
      sheet.removeEventListener("transitionend", onEnd);
      clearTimeout(fallbackTimer);
      this.#summary.classList.remove(SC_NAV_DRAWER_SUMMARY_CLOSING_CLASS);
      this.#details.classList.remove("sc-header-nav-drawer__details--closing");
      this.#details.removeAttribute("open");
    };

    const onEnd = (event) => {
      if (event.target !== sheet) return;
      if (event.propertyName !== "transform") return;
      complete();
    };

    sheet.addEventListener("transitionend", onEnd);
    const fallbackTimer = setTimeout(complete, SC_NAV_DRAWER_CLOSE_FALLBACK_MS);

    void sheet.offsetWidth;
  }

  #focusTarget() {
    const first = this.#panel?.querySelector('button, a[href], [href^="/"], [tabindex]:not([tabindex="-1"])');
    return first || this.#summary;
  }

  #resetNavState() {
    this.#branchHandle = null;
    this.#stack = [];
    this.#currentItems = [];
    this.#pageTitle = "";
    this.#syncSwitcherUi();
    this.#syncMedia();
  }

  #renderInitialRoot() {
    this.#paneTransitionActive = false;
    this.#queuedPaneSwap = null;
    this.#activePaneIndex = 0;
    if (!this.#paneEls.length) return;
    const html = this.#buildRootHtml();
    this.#paneEls[0].innerHTML = html;
    this.#paneEls[0].hidden = false;
    if (this.#paneEls[1]) {
      this.#paneEls[1].innerHTML = "";
      this.#paneEls[1].hidden = true;
    }
    for (const pane of this.#paneEls) {
      pane.classList.remove(SC_NAV_PANE_OUTGOING_OVERLAY_CLASS);
    }
    this.#syncMedia();
    this.#syncSwitcherUi();
  }

  #buildRootHtml() {
    const rootFlat = this.#data.rootFlat || [];
    let html = '<div class="sc-header-nav-drawer__pane-inner sc-header-nav-drawer__pane-inner--root">';
    html += '<div class="sc-header-nav-drawer__subhead sc-header-nav-drawer__subhead--root"></div>';
    html += '<ul class="sc-header-nav-drawer__list" role="list">';
    for (const row of rootFlat) {
      html += `<li class="sc-header-nav-drawer__item"><a class="sc-header-nav-drawer__link" href="${this.#escapeAttr(row.url)}"${
        row.current ? ' aria-current="page"' : ""
      }>${this.#escapeHtml(row.title)}</a></li>`;
    }
    html += "</ul></div>";
    return html;
  }

  #buildBranchHtml() {
    let html = '<div class="sc-header-nav-drawer__pane-inner">';
    html += '<div class="sc-header-nav-drawer__subhead">';
    html += `<button type="button" class="sc-header-nav-drawer__back" data-action="back" aria-label="${this.#escapeAttr(this.#labels.back)}"><span class="sc-header-nav-drawer__back-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="transform:scaleX(-1)"><path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span></button>`;
    html += `<h2 class="sc-header-nav-drawer__title">${this.#escapeHtml(this.#pageTitle)}</h2>`;
    html += "</div>";
    html += '<ul class="sc-header-nav-drawer__list" role="list">';
    for (const row of this.#currentItems) {
      const hasNested = row.nested && row.nested.length;
      if (hasNested) {
        html += `<li class="sc-header-nav-drawer__item"><button type="button" class="sc-header-nav-drawer__drill" data-action="drill" data-handle="${this.#escapeAttr(row.handle)}">${this.#escapeHtml(row.title)}<span class="sc-header-nav-drawer__chevron" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span></button></li>`;
      } else {
        html += `<li class="sc-header-nav-drawer__item"><a class="sc-header-nav-drawer__link" href="${this.#escapeAttr(row.url)}"${
          row.current ? ' aria-current="page"' : ""
        }>${this.#escapeHtml(row.title)}</a></li>`;
      }
    }
    html += "</ul></div>";
    return html;
  }

  #buildGrandchildHtml(items, title) {
    let html = '<div class="sc-header-nav-drawer__pane-inner">';
    html += '<div class="sc-header-nav-drawer__subhead">';
    html += `<button type="button" class="sc-header-nav-drawer__back" data-action="back" aria-label="${this.#escapeAttr(this.#labels.back)}"><span class="sc-header-nav-drawer__back-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="transform:scaleX(-1)"><path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span></button>`;
    html += `<h2 class="sc-header-nav-drawer__title">${this.#escapeHtml(title)}</h2>`;
    html += "</div>";
    html += '<ul class="sc-header-nav-drawer__list" role="list">';
    for (const row of items) {
      html += `<li class="sc-header-nav-drawer__item"><a class="sc-header-nav-drawer__link" href="${this.#escapeAttr(row.url)}"${
        row.current ? ' aria-current="page"' : ""
      }>${this.#escapeHtml(row.title)}</a></li>`;
    }
    html += "</ul></div>";
    return html;
  }

  #escapeHtml(str) {
    const d = document.createElement("div");
    d.textContent = str == null ? "" : String(str);
    return d.innerHTML;
  }

  #escapeAttr(str) {
    return this.#escapeHtml(str).replace(/"/g, "&quot;");
  }

  #getSwitcherByHandle(handle) {
    return (this.#data.switcher || []).find((s) => s.handle === handle);
  }

  #enterBranch(handle, direction) {
    const sw = this.#getSwitcherByHandle(handle);
    if (!sw || !sw.children) return;

    this.#branchHandle = handle;
    this.#stack = [];
    this.#currentItems = sw.children;
    this.#pageTitle = sw.title;

    const html = this.#buildBranchHtml();
    this.#swapPaneContent(() => html, direction, false);
  }

  #navigateDrill(handle) {
    const row = this.#currentItems.find((r) => r.handle === handle);
    if (!row || !row.nested || !row.nested.length) return;

    this.#stack.push({
      pageTitle: this.#pageTitle,
      items: this.#currentItems,
    });
    this.#currentItems = row.nested;
    this.#pageTitle = row.title;

    const html = this.#buildGrandchildHtml(row.nested, row.title);
    this.#swapPaneContent(() => html, "forward", false);
  }

  #navigateBack() {
    if (this.#stack.length > 0) {
      const prev = this.#stack.pop();
      this.#pageTitle = prev.pageTitle;
      this.#currentItems = prev.items;
      const html = this.#buildBranchHtml();
      this.#swapPaneContent(() => html, "back", false);
      return;
    }

    if (this.#branchHandle) {
      this.#navigateSwitcherToRoot();
    }
  }

  /** Root / unisex view — same end state as back when only one level deep inside a branch. */
  #navigateSwitcherToRoot() {
    if (!this.#branchHandle) return;
    this.#branchHandle = null;
    this.#stack = [];
    this.#currentItems = [];
    this.#pageTitle = "";
    const html = this.#buildRootHtml();
    this.#swapPaneContent(() => html, "back", false);
  }

  #getAllAnimClasses() {
    return [
      "sc-header-nav-drawer__pane--anim-out-forward",
      "sc-header-nav-drawer__pane--anim-in-forward",
      "sc-header-nav-drawer__pane--anim-out-back",
      "sc-header-nav-drawer__pane--anim-in-back",
    ];
  }

  #clearBelowShiftStyles(belowEl) {
    if (!belowEl) return;
    belowEl.style.transition = "";
    belowEl.style.transform = "";
  }

  #clearMediaFadeStyles(mediaRoot) {
    if (!mediaRoot) return;
    mediaRoot.style.transition = "";
    mediaRoot.style.opacity = "";
  }

  /**
   * Which promo variant is currently shown (`""` = root / unisex), or none.
   */
  #getMediaSlotKeyVisible(root) {
    const vis = root.querySelector(".sc-mobile-nav-media__variant--visible");
    if (!vis) return "__none__";
    return vis.getAttribute("data-media-for-handle") ?? "";
  }

  /**
   * Which promo slot applies for this branch (`""` = root), or `__none__` if no DOM variant (blank).
   */
  #getMediaSlotKeyForBranch(root, branchHandle) {
    const h = branchHandle || "";
    for (const el of root.querySelectorAll("[data-media-for-handle]")) {
      const v = el.getAttribute("data-media-for-handle") || "";
      if (v === h) return h;
    }
    return "__none__";
  }

  /**
   * Same metaobject slot as now → no fade. Different slot or blank ↔ promo → fade.
   */
  #isMediaSlotUnchanged(navMediaRoot) {
    if (!navMediaRoot) return true;
    return (
      this.#getMediaSlotKeyVisible(navMediaRoot) ===
      this.#getMediaSlotKeyForBranch(navMediaRoot, this.#branchHandle)
    );
  }

  /**
   * When the slot changes: hide promo instantly, sync, then fade in (layout matches new content before below-shift).
   */
  #syncSwitcherAndMediaForPaneSwap(navMediaRoot, mediaSlotUnchanged) {
    this.#syncSwitcherUi();

    if (mediaSlotUnchanged || !navMediaRoot || this.#reducedMotion.matches) {
      this.#syncMedia();
      return;
    }

    navMediaRoot.style.transition = "none";
    navMediaRoot.style.opacity = "0";
    this.#syncMedia();
    void navMediaRoot.offsetHeight;
    navMediaRoot.style.transition = `opacity ${SC_NAV_MEDIA_FADE_MS}ms ease`;
    navMediaRoot.style.opacity = "1";
  }

  /**
   * Same-document updates can move `.sc-header-nav-drawer__below` in layout; VT on
   * elements inside overflow scroll is unreliable. Compensate with transform, then
   * animate to 0 in lockstep with the pane slide (see SC_NAV_PANE_ANIM_MS).
   */
  #applyBelowShiftAnimation(belowEl, beforeRect) {
    if (!belowEl || !beforeRect) return;

    void belowEl.offsetHeight;
    const afterRect = belowEl.getBoundingClientRect();
    const dy = beforeRect.top - afterRect.top;
    if (Math.abs(dy) < 0.5) return;

    belowEl.style.transition = "none";
    belowEl.style.transform = `translateY(${dy.toFixed(2)}px)`;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        belowEl.style.transition = `transform ${SC_NAV_PANE_ANIM_MS}ms ${SC_NAV_BELOW_SHIFT_EASING}`;
        belowEl.style.transform = "translateY(0)";
      });
    });
  }

  /**
   * Pane swaps: follow web.dev SPA pattern — snapshot old view, run `update` once,
   * then animate — with optional `types` for directional styling (see
   * `html:active-view-transition-type(...)` in CSS).
   * @returns {ViewTransition | null}
   */
  #startViewTransitionForPaneUpdate(updateSync, direction) {
    if (!this.#shouldEnableViewTransitions()) {
      updateSync();
      return null;
    }

    const type =
      direction === "forward" ? VIEW_TRANSITION_TYPE_SC_NAV_FORWARD : VIEW_TRANSITION_TYPE_SC_NAV_BACK;

    try {
      return document.startViewTransition({
        types: [type],
        update: updateSync,
      });
    } catch {
      return document.startViewTransition(updateSync);
    }
  }

  /**
   * Same-document View Transitions for instant drawer open (no pane motion).
   * @returns {ViewTransition | null}
   */
  #runViewTransition(updateSync) {
    if (!this.#shouldEnableViewTransitions()) {
      updateSync();
      return null;
    }

    return document.startViewTransition(() => {
      updateSync();
    });
  }

  #swapPaneContent(buildHtml, direction, instant) {
    if (!this.#paneEls.length) return;

    const useMotion = !instant && !this.#reducedMotion.matches;

    const scrollEl = this.querySelector(".sc-header-nav-drawer__scroll");
    if (scrollEl) scrollEl.scrollTop = 0;

    const outgoing = this.#paneEls[this.#activePaneIndex];
    const incoming = this.#paneEls[1 - this.#activePaneIndex];
    const animClasses = this.#getAllAnimClasses();

    if (!useMotion) {
      this.#runViewTransition(() => {
        outgoing.innerHTML = buildHtml();
        this.#syncSwitcherUi();
        this.#syncMedia();
        setTimeout(() => trapFocus(this.#panel, this.#focusTarget()));
      });
      return;
    }

    if (this.#paneTransitionActive) {
      this.#queuedPaneSwap = { buildHtml, direction };
      return;
    }

    let finishedCleanup = false;
    const done = () => {
      if (finishedCleanup) return;
      finishedCleanup = true;
      this.#paneTransitionActive = false;
      outgoing.classList.remove(...animClasses);
      incoming.classList.remove(...animClasses);
      outgoing.classList.remove(SC_NAV_PANE_OUTGOING_OVERLAY_CLASS);
      outgoing.innerHTML = "";
      outgoing.hidden = true;
      incoming.hidden = false;
      this.#activePaneIndex = 1 - this.#activePaneIndex;
      const scrollEl = this.querySelector(".sc-header-nav-drawer__scroll");
      if (scrollEl) scrollEl.scrollTop = 0;
      setTimeout(() => trapFocus(this.#panel, this.#focusTarget()));

      const pending = this.#queuedPaneSwap;
      this.#queuedPaneSwap = null;
      if (pending) {
        queueMicrotask(() => this.#swapPaneContent(pending.buildHtml, pending.direction, false));
      }
    };

    this.#paneTransitionActive = true;

    const belowEl = this.querySelector(".sc-header-nav-drawer__below");
    const beforeBelowRect = belowEl?.getBoundingClientRect() ?? null;
    const navMediaRoot = this.querySelector("[data-nav-media]");
    const mediaSlotUnchanged = this.#isMediaSlotUnchanged(navMediaRoot);

    const runPaneUpdate = () => {
      incoming.innerHTML = buildHtml();
      incoming.hidden = false;
      outgoing.classList.add(SC_NAV_PANE_OUTGOING_OVERLAY_CLASS);
      this.#syncSwitcherAndMediaForPaneSwap(navMediaRoot, mediaSlotUnchanged);

      outgoing.classList.remove(...animClasses);
      incoming.classList.remove(...animClasses);

      void outgoing.offsetWidth;

      if (direction === "forward") {
        outgoing.classList.add("sc-header-nav-drawer__pane--anim-out-forward");
        incoming.classList.add("sc-header-nav-drawer__pane--anim-in-forward");
      } else {
        outgoing.classList.add("sc-header-nav-drawer__pane--anim-out-back");
        incoming.classList.add("sc-header-nav-drawer__pane--anim-in-back");
      }

      this.#applyBelowShiftAnimation(belowEl, beforeBelowRect);
    };

    runPaneUpdate();
    const paneVt = null;

    let outDone = false;
    let inDone = false;

    const tryDone = () => {
      if (!outDone || !inDone) return;
      outgoing.removeEventListener("animationend", onOut);
      incoming.removeEventListener("animationend", onIn);
      if (fallbackTimer) clearTimeout(fallbackTimer);

      const finishDomCleanup = () => {
        this.#clearBelowShiftStyles(this.querySelector(".sc-header-nav-drawer__below"));
        this.#clearMediaFadeStyles(this.querySelector("[data-nav-media]"));
        done();
      };

      if (paneVt && typeof paneVt.finished?.then === "function") {
        paneVt.finished.then(finishDomCleanup).catch(finishDomCleanup);
        return;
      }

      finishDomCleanup();
    };

    const onOut = (ev) => {
      if (ev.target !== outgoing) return;
      if (!String(ev.animationName || "").includes("scNavPane")) return;
      outDone = true;
      tryDone();
    };

    const onIn = (ev) => {
      if (ev.target !== incoming) return;
      if (!String(ev.animationName || "").includes("scNavPane")) return;
      inDone = true;
      tryDone();
    };

    outgoing.addEventListener("animationend", onOut);
    incoming.addEventListener("animationend", onIn);

    const fallbackTimer = setTimeout(() => {
      if (outDone && inDone) return;
      outDone = true;
      inDone = true;
      tryDone();
    }, SC_NAV_PANE_SWAP_FALLBACK_MS);
  }

  #syncSwitcherUi() {
    if (!this.#switcherRow) return;
    const buttons = this.#switcherRow.querySelectorAll("[data-switcher-handle]");
    for (const btn of buttons) {
      const h = btn.getAttribute("data-switcher-handle");
      const active = Boolean(this.#branchHandle && h === this.#branchHandle);
      if (active) {
        btn.setAttribute("aria-current", "true");
      } else {
        btn.removeAttribute("aria-current");
      }
      btn.disabled = false;
      btn.removeAttribute("aria-disabled");
    }
  }

  #syncMedia() {
    const root = this.querySelector("[data-nav-media]");
    if (!root) return;
    const handle = this.#branchHandle || "";
    root.setAttribute("data-active-handle", handle);
    root.querySelectorAll("[data-media-for-handle]").forEach((el) => {
      const v = el.getAttribute("data-media-for-handle") || "";
      const on = v === handle;
      el.classList.toggle("sc-mobile-nav-media__variant--visible", on);
      el.setAttribute("data-visible", on ? "true" : "false");
      if (on) {
        const img = el.querySelector("img");
        if (img && typeof img.decode === "function") {
          img.decode().catch(() => {});
        }
      }
    });
  }
}

customElements.define("sc-header-nav-drawer", ScHeaderNavDrawer);
